import { Component, OnInit, AfterViewInit, ElementRef, ViewChildren, QueryList, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { DopplerService } from './service/doppler.service';
import { AnalyticsService } from './service/analytics.service';
import { Router } from '@angular/router';

interface FaqItem {
  pregunta: string;
  respuesta: string;
  isOpen: boolean;
}

interface EmpresaLogo {
  nombre: string;
  imagen: string;
}

@Component({
  selector: 'app-landing-dea',
  templateUrl: './landing-dea.component.html',
  styleUrls: ['./landing-dea.component.css']
})
export class LandingDeaComponent implements OnInit, AfterViewInit {
  deaForm: FormGroup;
  isMobileMenuOpen: boolean = false;
  public isMobile: boolean;
  public isSubmitted: boolean = false;
  public isSubmitting: boolean = false;
  public submitError: string = '';

  // FAQ Items
  faqItems: FaqItem[] = [
    {
      pregunta: '¿Qué significa certificar un espacio cardioprotegido?',
      respuesta: 'Significa que el lugar cuenta con un DEA autorizado según normativa vigente y con personal capacitado para actuar ante una emergencia cardiovascular. De esta forma, se garantiza una respuesta rápida y efectiva, cumpliendo con los estándares de calidad necesarios para salvar vidas.',
      isOpen: false
    },
    {
      pregunta: '¿Cómo es el proceso de instalación de los equipos DEA?',
      respuesta: 'Coordinamos con vos el día y horario que mejor se adapte a tu empresa. Nuestro equipo técnico se encarga de la instalación completa, siguiendo todas las normativas vigentes. Incluye la colocación de la cartelería que identifica el espacio como cardioprotegido y un gabinete con alarma sonora y visual, donde queda resguardado el DEA.',
      isOpen: false
    },
    {
      pregunta: '¿Cuentan con servicio postventa?',
      respuesta: 'Sí, en vittal te acompañamos también después de la compra. Nuestro servicio postventa incluye una visita cada 6 meses para revisar el correcto funcionamiento del DEA: controlamos la batería, el sistema de geolocalización y el software. Así, te asegurás de que el equipo esté siempre listo para usar ante cualquier emergencia.',
      isOpen: false
    }
  ];

  // Logos de empresas
  empresasLogos: EmpresaLogo[] = [
    { nombre: 'Danone', imagen: 'danone.png' },
    { nombre: 'Mercado Libre', imagen: 'meli.png' },
    { nombre: 'La Serenisima', imagen: 'serenisima.png' },
    { nombre: 'TyC Sports', imagen: 'tycsports.png' },
    { nombre: 'UCES', imagen: 'uces.png' },
    { nombre: 'Walmart', imagen: 'walmart.png' },
    { nombre: 'Villavicencio', imagen: 'villavicencio.png' }
  ];

  @ViewChildren('logosSlider') logosSlider!: QueryList<ElementRef>;

  constructor(
    private fb: FormBuilder,
    private dopplerService: DopplerService,
    private analyticsService: AnalyticsService,
    private elementRef: ElementRef,
    private router: Router
  ) {
    this.deaForm = this.fb.group({
      nombre: ['', Validators.required],
      apellido: ['', Validators.required],
      empresa: [''],
      telefono: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      localidad: [''],
      magnet: [false]
    });
  }

  ngOnInit(): void {
    this.checkDeviceType();
    this.initializeAnalytics();
  }

  ngAfterViewInit(): void {
    this.initializeLogosSlider();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.checkDeviceType();
    if (!this.isMobile) {
      this.closeMenu();
    }
  }

  private checkDeviceType() {
    this.isMobile = window.innerWidth < 768;
  }

  private initializeAnalytics() {
    this.analyticsService.initializeAnalytics();
  }

  private initializeLogosSlider(): void {
    if (this.logosSlider.first) {
      const sliderElement = this.logosSlider.first.nativeElement;
      const logoCount = this.empresasLogos.length;
      const duration = logoCount * 3;

      sliderElement.style.animationDuration = `${duration}s`;
      sliderElement.style.visibility = 'visible';

      setInterval(() => {
        const computed = window.getComputedStyle(sliderElement);
        if (computed.animationPlayState === 'paused') {
          sliderElement.style.animationPlayState = 'running';
        }
      }, 2000);
    }
  }

  toggleMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    document.body.style.overflow = this.isMobileMenuOpen ? 'hidden' : '';
  }

  closeMenu(): void {
    this.isMobileMenuOpen = false;
    document.body.style.overflow = '';
  }

  toggleFaq(item: FaqItem): void {
    this.faqItems.forEach(faq => {
      if (faq !== item) {
        faq.isOpen = false;
      }
    });
    item.isOpen = !item.isOpen;
  }

  getEmailErrorMessage(): string {
    const emailControl = this.deaForm.get('email');
    if (emailControl && emailControl.hasError('required')) {
      return 'El email es obligatorio';
    }
    if (emailControl && emailControl.hasError('email')) {
      return 'Por favor, ingrese un email válido';
    }
    return '';
  }

  onSubmit(): void {
    if (this.deaForm.valid) {
      this.isSubmitting = true;
      this.submitError = '';

      this.dopplerService.submitForm(this.deaForm.value).subscribe(
        response => {
          this.isSubmitted = true;
          this.isSubmitting = false;
          this.analyticsService.trackFormSubmission('success');
          this.deaForm.reset();
          this.router.navigate(['/gracias-landing-dea']);
        },
        error => {
          this.isSubmitting = false;
          console.error('Error submitting form:', error);
          this.analyticsService.trackFormSubmission('error');
          this.submitError = 'Hubo un error al enviar el formulario. Por favor, intenta nuevamente.';
          if (error.status === 400) {
            this.submitError = 'El email ingresado no es válido o ya está registrado.';
          }
        }
      );
    } else {
      Object.keys(this.deaForm.controls).forEach(key => {
        const control = this.deaForm.get(key);
        if (control && control.invalid) {
          control.markAsTouched();
        }
      });
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    const mobileMenu = this.elementRef.nativeElement.querySelector('.mobile-menu');
    const hamburger = this.elementRef.nativeElement.querySelector('.hamburger-menu');

    if (mobileMenu && hamburger && this.isMobileMenuOpen) {
      if (!mobileMenu.contains(target) && !hamburger.contains(target)) {
        this.closeMenu();
      }
    }
  }

  scrollToSection(sectionId: string, event: Event): void {
    event.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    if (this.isMobileMenuOpen) {
      this.closeMenu();
    }
  }

  descargarGuia(event: Event): void {
    event.preventDefault();
    this.deaForm.patchValue({ magnet: true });
    const formularioElement = document.getElementById('formulario');
    if (formularioElement) {
      formularioElement.scrollIntoView({ behavior: 'smooth' });
    }
  }
}